/**
 * `src/platform/` — the refresh token's home, which is now module memory and, for one release,
 * the legacy `localStorage` key it is migrating out of (decision D24, rulings R-10 and R-15).
 *
 * ## What changed and why
 *
 * The console used to persist its refresh token under `zion.cpadmin.refresh_token`, where any
 * script on `admin.zion.witness.ai` could read it. cp-auth-api now sets `cp_refresh_token` as an
 * HttpOnly cookie under `X-Zion-Session-Transport: cookie`, so the credential is no longer
 * readable by script at all. This file is what carries a browser across that line exactly once.
 *
 * ⛔ ONE VARIABLE, SIX ENTRY POINTS, AND THE SPLIT IS THE MECHANISM (R-10, R-99). On boot
 * `hasStoredLegacyRefreshToken` only looks at the key, for the R-70 guard. The first refresh then
 * runs `migrateLegacyRefreshToken` inside the refresh lock, which moves the stored token into
 * `legacyRefreshToken` and deletes the key, and every later refresh re-reads the key through the
 * same call, because a second tab may have rotated it. `getLegacyRefreshToken` is what `doRefresh`
 * sends in the body until a cookie response arrives, so a network failure, a 5xx or a rejected
 * 200 costs a retry and not the session. `storeLegacyRefreshToken` writes a rotated token back
 * when an N-1 cp-auth-api pod answers a token pair. `reloadLegacyRefreshToken` re-reads the key
 * without deleting it, for the revoke inside `logout`. `clearLegacyRefreshToken` ends it.
 *
 * ⛔ TWO `localStorage.setItem` CALLS IN THIS PACKAGE CARRY THE LEGACY KEY, AND ONLY ONE OF THEM
 * WRITES A TOKEN (D29). `storeLegacyRefreshToken` writes the rotated token;
 * `src/__tests__/storage-census.test.ts` allows that one symbol in this one file for the legacy
 * key alone (R-86), and a planted control must fail the census. `clearLegacyRefreshToken` writes
 * the empty string over the key when `removeItem` throws (R-89), which erases the token rather
 * than storing one.
 *
 * ⛔ A DISTINCT KEY FROM THE OLD CONSOLE'S `cp_refresh_token`, still (D-6′ / D-9′). `localStorage`
 * is origin-scoped and the old console wrote `cp_refresh_token`; sharing a rotation-sensitive slot
 * on one origin means each refresh invalidates the other session. The key is read and deleted here
 * and written back only on the N-1 arm, so the window in which either console could contend is one
 * release long and the name still must not collide. `src/shell/__tests__/no-old-console-dependency.test.tsx`
 * observes the outcome rather than grepping for the literal, because `'cp_' + 'refresh_token'`
 * evades every text scan.
 */
// The refresh cookie's lifetime. ⚠️ A PACKAGE specifier, so the relative-import style of this
// subtree does not apply to it. The marker below expires with the cookie, so both sides of that
// pair read one constant (R-78).
import { REFRESH_COOKIE_MAX_AGE_SECONDS } from '@zion/sdk-contracts';

/**
 * The `localStorage` key a pre-cookie console wrote the refresh token under.
 *
 * Kept for two reasons and no others: `migrateLegacyRefreshToken` reads and deletes it once per
 * document, inside the refresh lock (R-99), and `storeLegacyRefreshToken` writes it back when an
 * N-1 pod rotates the pair (D24, R-10).
 * Renamed from `REFRESH_TOKEN_KEY` so a reader cannot mistake it for where the session lives now.
 */
export const LEGACY_REFRESH_TOKEN_KEY = 'zion.cpadmin.refresh_token';

/**
 * The marker that says this browser has a session and names the instant that session's cookie
 * dies (rulings R-70 and R-78).
 *
 * ⛔ IT IS WHAT STOPS A PLANTED COOKIE FROM SIGNING AN OPERATOR IN AS SOMEBODY ELSE (CS5-1).
 * `cp_refresh_token` is host-only, but every origin under `witness.ai` is same-site, so a script
 * on a sibling origin can set `cp_refresh_token=<its own token>; Domain=witness.ai`. R-11 refuses
 * a `Cookie` header carrying the name twice, and that covers a browser that already holds its
 * own. It does not cover a signed-out browser, where the planted pair is the only pair: D24's
 * unconditional boot refresh would redeem the attacker's token, cp-auth-api would rotate the
 * attacker's session, and the console would render the attacker's identity to the operator.
 *
 * So a refresh goes out only when this browser has something to ask about. `useAuth`'s mount
 * effect refreshes when this key holds an instant in the future, or when
 * `hasStoredLegacyRefreshToken` sees a token; with neither, it settles signed out and sends no
 * request at all. ⛔ AND EVERY LATER REFRESH ASKS THE SAME QUESTION INSIDE THE LOCK (R-100).
 * `runRefresh` sends only when this tab holds a bearer, a legacy token in module memory, or a
 * live marker, so a 401 replay after sign-out cannot redeem a planted cookie either.
 *
 * ⛔ IT CARRIES NO CREDENTIAL, and that is what makes it safe to store where the refresh token
 * may not be. The value is one number: the epoch millisecond after which the refresh cookie is
 * gone, `Date.now() + REFRESH_COOKIE_MAX_AGE_SECONDS * 1000`. Reading it tells a script that a
 * session exists, which a script in this document can already see from the rendered shell.
 * Writing it makes a tab try one refresh, which answers 401 without a real cookie.
 *
 * ⛔ THE INSTANT IS WHAT STOPS THE MARKER OUTLIVING THE COOKIE (R-78). A bare flag said "this
 * origin once had a session" for as long as the browser kept it, so 30 days after the last mint
 * the cookie was gone and the marker still sent the boot refresh — and a sibling-origin plant was
 * then the sole pair. `setTokens` calls `markSessionPresent` on every mint and every successful
 * refresh, so the instant slides forward exactly as the cookie's `Max-Age` does, and a browser
 * that stopped visiting expires on the same day on both sides.
 *
 * ⛔ AND A SIBLING ORIGIN CANNOT SET IT. `localStorage` is origin-scoped with no `Domain`
 * equivalent, so `admin.zion.witness.ai`'s storage is reachable only from that origin. That
 * asymmetry between Web Storage and cookies is the whole mechanism: the attacker can plant the
 * cookie and cannot write the marker that makes anyone send it.
 *
 * The D29 census allows this key by name. It is a marker, not a token.
 */
export const SESSION_PRESENT_KEY = 'zion.cpadmin.session_present';

/**
 * The refresh token this tab still owes cp-auth-api in a request body, or `null` once the tab is
 * on the cookie arm or never held one.
 *
 * Module memory, so it dies with the document. A reload then hydrates from the cookie, which is
 * the whole point of the move.
 */
let legacyRefreshToken: string | null = null;

/** Latches the one-shot storage warning in `storeLegacyRefreshToken`. */
let warnedOnWriteFailure = false;

/**
 * Whether this document still owes the one-time delete of the legacy key (R-99).
 *
 * `true` from module load until `migrateLegacyRefreshToken` has removed the key, or until
 * `storeLegacyRefreshToken` or `clearLegacyRefreshToken` has made the key's content this tab's
 * own. After that the key holds an N-1 write-back or nothing, and deleting it would throw away
 * the only copy that survives a reload.
 */
let legacyKeyDeleteOwed = true;

/**
 * Does the legacy key hold a token right now? A read that deletes nothing (R-99).
 *
 * The one caller is `useAuth`'s mount effect, for the R-70 guard: a browser that last ran the
 * pre-cookie console holds a token and no session marker, and the boot has to ask for it. The
 * move into module memory and the delete wait for `migrateLegacyRefreshToken` inside the refresh
 * lock, so a value seen here before the wait is never the one sent.
 *
 * @returns `true` for a stored non-empty string, `false` for an absent key, the empty string
 *   `clearLegacyRefreshToken` leaves on a refused removal (R-89), and an unreadable store.
 * @throws nothing. An unreadable store reads as no token, which fails toward asking nothing.
 *
 * Side effects: one `localStorage.getItem`. The module variable and the key are untouched.
 */
export function hasStoredLegacyRefreshToken(): boolean {
  try {
    const stored = localStorage.getItem(LEGACY_REFRESH_TOKEN_KEY);
    return stored !== null && stored !== '';
  } catch {
    return false;
  }
}

/**
 * Adopt the legacy key into module memory, and delete the key the first time (R-10, R-47, R-99).
 *
 * The one caller is `useAuth`'s `runRefresh`, inside `runExclusive(REFRESH_LOCK_NAME, ...)`,
 * immediately before the request. The token then rides the refresh body under the transport
 * header on every attempt until a `CpCookieSessionResponse` arrives.
 *
 * ⛔ THE MIGRATION RUNS INSIDE THE LOCK, AND THAT IS WHAT KEEPS A PEER'S SUCCESSOR (R-99). It
 * used to run in the mount effect, before the lock. During an N-1 roll tab B read `r0` there,
 * tab A rotated under the lock and persisted `r1`, and B's delete then removed `r1`, the only
 * cross-tab copy of the live successor. B sent the spent `r0` and both tabs signed out. Inside
 * the lock the read and the delete both follow A's release, so B adopts `r1` and sends it.
 *
 * ⛔ ONE READ, TWO JOBS. The first call in a document is the migration: it adopts and deletes.
 * Every later call is the re-read, because a second tab may have rotated the key while this one
 * waited: it adopts a present key and deletes nothing. An N-1 write-back has to survive a reload
 * and a peer's re-read, so only the first call deletes, and a `storeLegacyRefreshToken` or
 * `clearLegacyRefreshToken` before that first call cancels the delete.
 *
 * ⛔ AN ABSENT KEY LEAVES THE VARIABLE ALONE, AND THAT IS WHERE THE CONSOLE DIVERGES FROM THE DP
 * SDK'S SAME-NAMED RELOAD HELPER. The SDK clears on an absent key, because its caller is a
 * `token-refreshed` channel message and an absent key there means a peer converted to the cookie.
 * The console has no channel (D26), so this runs on EVERY refresh, including the ones after this
 * call deleted the key it had just drained. Clearing there would throw away the migrating
 * browser's only credential and sign the operator out, which is acceptance criterion 5 inverted.
 * `clearLegacyRefreshToken` is what ends the variable's life, on this tab's own cookie response.
 *
 * @returns nothing. A caller reads the adopted token with `getLegacyRefreshToken()`.
 * @throws nothing. A `localStorage` that throws on read or remove — private mode, a quota
 *   failure, a hostile override — leaves the module variable as it was and is silent: there is
 *   nothing useful to do and no credential to leak from a store nobody can reach. A delete that
 *   did not happen stays owed, so the next call tries it again.
 *
 * Side effects: the module variable takes the stored value when it is a non-empty string, then,
 * on the owed call, `localStorage.removeItem` runs. Two `useAuth` trees in one document cannot
 * discard each other's token: the second tree's call finds the key gone and keeps the variable.
 */
export function migrateLegacyRefreshToken(): void {
  try {
    const stored = localStorage.getItem(LEGACY_REFRESH_TOKEN_KEY);
    if (stored !== null && stored !== '') legacyRefreshToken = stored;
    if (legacyKeyDeleteOwed) {
      localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
      legacyKeyDeleteOwed = false;
    }
  } catch {
    // "Cannot read the store" and "nothing is in it" lead to the same place: send what module
    // memory holds, or `{}`, and let the cookie, if one exists, be the credential.
  }
}

/**
 * The refresh token to put in the next request body, or `null` when the cookie is the credential.
 *
 * `doRefresh` reads it inside the refresh lock rather than taking it as an argument, so whichever
 * refresh runs first carries it and a coalesced second caller cannot drop it (R-10).
 *
 * @returns the migrated or written-back token, or `null`.
 * @throws nothing. Reads no storage.
 */
export function getLegacyRefreshToken(): string | null {
  return legacyRefreshToken;
}

/**
 * Adopt whatever the legacy key holds now, if it holds anything, and delete nothing.
 *
 * ⛔ THE SECRET NEVER TRAVELS BETWEEN TABS; THE LEGACY KEY IS THE SHARED STATE (D26, R-47). During
 * a mixed roll two tabs can both be on the body arm. Tab A refreshes, an N-1 pod rotates the pair
 * and hands the token back, and `storeLegacyRefreshToken` writes it to the key both tabs read. Tab
 * B's own module variable still holds the token A rotated away; presenting it past the replay
 * window is a refusal for B, and revoking with it revokes nothing (R-77). The one caller is
 * `useAuth`'s `logout`, inside `runExclusive`, immediately before the revoke; `clearAuth` removes
 * the key in the same critical section, so a read that deletes would buy that caller nothing.
 * `runRefresh` re-reads through `migrateLegacyRefreshToken` instead, which is this read plus the
 * one-time delete (R-99).
 *
 * ⛔ AN ABSENT KEY LEAVES THE VARIABLE ALONE, AND THAT IS WHERE THE CONSOLE DIVERGES FROM THE DP
 * SDK'S SAME-NAMED HELPER. The SDK clears on an absent key, because its caller is a
 * `token-refreshed` channel message and an absent key there means a peer converted to the cookie.
 * The console has no channel (D26). The key is absent here whenever the migration drained it and
 * no N-1 write-back followed, and clearing there would throw away the migrating browser's only
 * credential, which is acceptance criterion 5 inverted.
 *
 * The case the SDK's clear exists for is inert here. A tab can only hold a stale variable with no
 * key once some tab has received a cookie response, which means this browser holds the cookie —
 * and cp-auth-api reads the cookie first (R-12), so the stale body token is never the credential.
 * `clearLegacyRefreshToken` is what ends the variable's life, on this tab's own cookie response.
 *
 * @returns nothing.
 * @throws nothing. An unreadable store leaves the module variable untouched, which keeps the
 *   token this tab already migrated rather than discarding a live credential over a storage fault.
 *
 * Side effects: the module variable, and one `localStorage.getItem`.
 */
export function reloadLegacyRefreshToken(): void {
  try {
    const stored = localStorage.getItem(LEGACY_REFRESH_TOKEN_KEY);
    if (stored !== null && stored !== '') legacyRefreshToken = stored;
  } catch {
    // Keep what we have. Discarding a live body-arm credential because the store hiccuped would
    // cost the session on the one arm that cannot fall back to a cookie.
  }
}

/**
 * Keep `token` for the next attempt and write it back to `LEGACY_REFRESH_TOKEN_KEY`.
 *
 * The one caller is `useAuth`'s `setTokens` on a `CpTokenPairResponse`: an N-1 cp-auth-api pod
 * rotated the pair and set no cookie, so the rotated token is this tab's only credential, it has
 * to survive a reload, and a second tab's re-read inside the lock has to see it (R-10, R-47, D26).
 *
 * ⛔ THE ONE WEB STORAGE WRITE OF A TOKEN IN THIS PACKAGE (D29). The census allows this symbol in
 * this file for this key only (R-86). `clearLegacyRefreshToken`'s fallback writes the same key
 * and writes the empty string into it (R-89), which is an erasure.
 *
 * ⛔ THE RETURN VALUE IS WHAT GATES THE SESSION MARKER (ruling R-92). An N-1 pair carries no
 * `Set-Cookie`, so the token written here is the tab's ONLY credential across a reload. The
 * marker is a much smaller value under a different key, so a store that refuses this write can
 * still take that one; the next boot then read a live marker with no legacy token, sent a
 * cookie-only refresh, and a sibling-origin plant was the sole pair (CS5-1). `setTokens` raises
 * the marker on this arm only when this returns `true`. The module variable is set either way,
 * so the tab keeps working for its own lifetime, and the one-time warning below is unchanged.
 *
 * @param token the `refreshToken` of the token pair just parsed, a non-empty string by
 *   `cpTokenPairResponseSchema`.
 * @returns `true` when the Web Storage write succeeded, `false` when the store refused it. Both
 *   answers leave the module variable holding `token`.
 * @throws nothing. A failed write is non-fatal: the access token is already in module memory and
 *   the module variable above holds the rotated token, so the session works until the tab closes.
 *   It is surfaced once through `console.warn` rather than swallowed — an operator who is quietly
 *   signed out on every reload deserves something in the console — and once rather than per call,
 *   because this can run on every proactive refresh for the length of a roll and a repeating
 *   warning is noise nobody reads.
 */
export function storeLegacyRefreshToken(token: string): boolean {
  legacyRefreshToken = token;
  // The key holds this tab's own write-back from here on, so the one-time migration delete is
  // cancelled (R-99): deleting it would lose the only copy that survives a reload.
  legacyKeyDeleteOwed = false;
  try {
    localStorage.setItem(LEGACY_REFRESH_TOKEN_KEY, token);
    return true;
  } catch {
    if (!warnedOnWriteFailure) {
      warnedOnWriteFailure = true;
      console.warn(
        '[cp-admin] could not persist the refresh token; this session will not survive a reload',
      );
    }
    return false;
  }
}

/**
 * Forget the legacy token: the module variable and the key.
 *
 * Callers: `useAuth`'s `setTokens` on the first `CpCookieSessionResponse`, which is the moment
 * this tab is on the cookie arm for good, and `clearAuth`, so a revoked or refused credential is
 * never left readable (R-15). Idempotent.
 *
 * ⛔ A STORE THAT REFUSES THE REMOVAL GETS THE EMPTY STRING WRITTEN OVER THE TOKEN (ruling R-89).
 * The removal used to be the only attempt, so a refused `removeItem` left the old refresh token
 * readable by any script on the origin after a logout or a terminal 401, and the next boot's
 * `migrateLegacyRefreshToken` sent it again. The overwrite is the shape R-82 gave the session
 * marker, and it needs no new reader: every reader of this key already treats `''` as no token.
 * `reloadLegacyRefreshToken` and `migrateLegacyRefreshToken` each keep the stored value only for
 * `stored !== null && stored !== ''`, and `hasStoredLegacyRefreshToken` answers `false` for it,
 * so after the overwrite the next boot's guard, reload and migration find no token.
 *
 * @returns nothing.
 * @throws nothing. An unreachable store holds no token to leak. When the overwrite throws too
 *   the store refuses both verbs, nothing more can be done, and the function returns. It stays
 *   silent for the reason `clearSessionPresent` does: `storeLegacyRefreshToken` already warns
 *   once that the store is unreachable, and a second warning on the same fault is noise.
 */
export function clearLegacyRefreshToken(): void {
  legacyRefreshToken = null;
  // This removal is the delete the migration owed, so the migration owes nothing more (R-99).
  legacyKeyDeleteOwed = false;
  try {
    localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
  } catch {
    try {
      // The empty string is what both readers of this key already treat as no token.
      localStorage.setItem(LEGACY_REFRESH_TOKEN_KEY, '');
    } catch {
      // Both verbs refused. A store nobody can reach holds no token a script can read either.
    }
  }
}

/**
 * Record that this browser holds a session, and say when that session's cookie dies (R-70, R-78).
 *
 * The one caller is `useAuth`'s `setTokens`, so every mint writes it: login, passkey, the OAuth
 * exchange, a password change, and every successful refresh. Writing it on a refresh as well as
 * on a mint is what makes the marker slide: cp-auth-api re-sets the cookie with a fresh
 * `Max-Age` on the same events, so both expiries move together and neither outlives the other.
 *
 * @returns nothing.
 * @throws nothing. A refused write costs the next cold boot its refresh, so that browser signs
 *   in again. It is not worth a second warning: `storeLegacyRefreshToken` already says the store
 *   is unreachable, and a browser that cannot write cannot keep a session across a reload anyway.
 *
 * Side effects: one `localStorage.setItem` of `Date.now() + REFRESH_COOKIE_MAX_AGE_SECONDS *
 * 1000`, as a base-10 string.
 */
export function markSessionPresent(): void {
  try {
    localStorage.setItem(
      SESSION_PRESENT_KEY,
      String(Date.now() + REFRESH_COOKIE_MAX_AGE_SECONDS * 1000),
    );
  } catch {
    // See the docblock: the cost is one re-authentication, and the store is already broken.
  }
}

/**
 * Forget that this browser holds a session (R-70, R-82).
 *
 * Callers: `useAuth`'s `clearAuth`, which runs on logout and on a terminal 401. After either one
 * the next cold boot sends no refresh, which is what keeps a planted cookie unredeemable.
 *
 * ⛔ A STORE THAT REFUSES THE REMOVAL IS TOMBSTONED, NOT LEFT ALONE (ruling R-82). The removal
 * used to be the only attempt, so a refused `removeItem` left a live marker behind after a logout
 * or a terminal 401, and the next cold boot asked. With the host cookie gone, a sibling-origin
 * plant was the sole pair again — the CS5-1 case the marker exists to close. Writing the past
 * instant `'0'` closes it with the mechanism already in place: `hasSessionPresent` reads any
 * instant that is not in the future as no marker (R-78), so the next read answers false.
 *
 * @returns nothing.
 * @throws nothing. Idempotent. When the tombstone write throws too the store refuses both verbs,
 *   nothing more can be done, and the function returns. It stays silent for the reason
 *   `markSessionPresent` does: `storeLegacyRefreshToken` already warns once that the store is
 *   unreachable, and a second warning on the same fault is noise.
 */
export function clearSessionPresent(): void {
  try {
    localStorage.removeItem(SESSION_PRESENT_KEY);
  } catch {
    try {
      // `'0'` is the epoch, which is in the past for every clock this runs on.
      localStorage.setItem(SESSION_PRESENT_KEY, '0');
    } catch {
      // Both verbs refused. An unreachable store holds no flag the next boot can read either.
    }
  }
}

/**
 * Has this browser signed in, not signed out, and could its refresh cookie still be alive?
 *
 * Two callers in `useAuth`. The mount effect: a `false` here, with no legacy token in module
 * memory or in the key, means the boot sends no request (R-70). And `runRefresh`, inside the
 * refresh lock: a `false` here, with no legacy token in module memory, means that refresh sends
 * no request either, whoever called it, and a bearer in client memory does not change that
 * (R-100, R-103).
 *
 * ⛔ A PAST INSTANT IS NO MARKER, AND IT IS DELETED ON THE WAY OUT (R-78). The cookie is gone by
 * then, so a boot that still asked would send a bare request that only a planted cookie could
 * answer. Removing the stale value keeps the store honest for the next reader and makes the
 * absence observable to a test.
 *
 * @returns `true` only for a stored value that parses as a finite number strictly greater than
 *   `Date.now()`. An absent key, a past instant, an unparseable value and the pre-R-78 flag `1`
 *   all answer `false`; the last three are removed first. `Number('')` is `0`, so an empty value
 *   takes the past-instant arm.
 * @throws nothing. An unreadable store reads as no session, which fails toward asking nothing,
 *   and a store that refuses the removal answers `false` just the same.
 */
export function hasSessionPresent(): boolean {
  try {
    const stored = localStorage.getItem(SESSION_PRESENT_KEY);
    if (stored === null) return false;
    const expiresAt = Number(stored);
    if (Number.isFinite(expiresAt) && expiresAt > Date.now()) return true;
    localStorage.removeItem(SESSION_PRESENT_KEY);
    return false;
  } catch {
    return false;
  }
}

/** TEST-ONLY reset of the once-only warning latch above (R-64 keeps both the latch and this). */
export function __resetTokenWarningForTest(): void {
  warnedOnWriteFailure = false;
}

/**
 * TEST-ONLY reset of the module variable, and of the migration's owed delete (R-99).
 *
 * ⛔ SEPARATE FROM THE WARNING LATCH, and `src/__tests__/setup.ts` calls it after every test. The
 * token used to live in `localStorage`, which that file already clears; it lives in module memory
 * now, and vitest's `isolate: true` is per FILE, so without this a suite that signs in leaves a
 * body-arm token behind and the next test's "boots with nothing stored" arm silently exercises the
 * migration path instead.
 */
export function __resetLegacyTokenForTest(): void {
  legacyRefreshToken = null;
  legacyKeyDeleteOwed = true;
}
